import { useEffect, useState } from 'react';

import { isAdmin, isEditor } from '@core/util/util.isEditor';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@frontend/components/ui/select';
import {
  useDirectoryMe,
  useDirectoryTeam,
} from '@frontend/shared/hooks/useDirectory';

type RoleOverride = 'viewer' | 'editor' | 'admin' | null;

const ROLE_STORAGE_KEY = 'dev-role-override';

// Get actual user roles without override
function useActualUserRoles() {
  const me = useDirectoryMe();
  const team = useDirectoryTeam();

  const actualIsAdmin = isAdmin(team, me.id);
  const actualIsEditor = isEditor(team, me.id);

  return {
    isAdmin: actualIsAdmin,
    isEditor: actualIsEditor,
    role: actualIsAdmin ? 'admin' : actualIsEditor ? 'editor' : 'viewer',
  };
}

export function RoleSelector() {
  const [selectedRole, setSelectedRole] = useState<string>('user');
  const { role: actualRole } = useActualUserRoles();

  useEffect(() => {
    const stored = sessionStorage.getItem(ROLE_STORAGE_KEY);
    if (stored) {
      setSelectedRole(stored);
    }
  }, []);

  const handleRoleChange = (value: string) => {
    setSelectedRole(value);

    if (value === 'user') {
      sessionStorage.removeItem(ROLE_STORAGE_KEY);
    } else {
      sessionStorage.setItem(ROLE_STORAGE_KEY, value);
    }

    // Force a page reload to apply the new role
    window.location.reload();
  };

  // Only show in non-production environments
  // Check if we're not in production based on common indicators
  const isDev = window.location.hostname !== 'questionbase.ai';

  if (!isDev) {
    return null;
  }

  return (
    <div className="fixed bottom-4 right-4 z-50">
      <div className="bg-white border border-gray-300 rounded-lg shadow-lg p-3">
        <label
          htmlFor="role-selector"
          className="block text-xs font-medium text-gray-700 mb-1"
        >
          Dev Role Override
        </label>
        <Select value={selectedRole} onValueChange={handleRoleChange}>
          <SelectTrigger className="w-[160px] h-8">
            <SelectValue placeholder="Select role" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="user">User Role ({actualRole})</SelectItem>
            <SelectItem value="viewer">Force Viewer Role</SelectItem>
            <SelectItem value="editor">Force Editor Role</SelectItem>
            <SelectItem value="admin">Force Admin Role</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}

export function getRoleOverride(): RoleOverride {
  if (typeof window === 'undefined') return null;

  const stored = sessionStorage.getItem(ROLE_STORAGE_KEY);
  return (stored as RoleOverride) || null;
}
